import React from 'react';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  AdminModule,
  AdminPermissions,
  ALL_MODULES,
  MODULE_LABELS,
  PermissionAction,
  moduleHasAction,
  toggleModuleAction,
} from '../../utils/adminPermissions';

const ACTION_LABELS: Record<PermissionAction, string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
};

interface PermissionMatrixProps {
  modules: AdminPermissions['modules'];
  onChange: (modules: AdminPermissions['modules']) => void;
  readOnly?: boolean;
  compareModules?: AdminPermissions['modules'];
  disabledModules?: AdminModule[];
}

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  modules,
  onChange,
  readOnly = false,
  compareModules,
  disabledModules,
}) => {
  const actions: PermissionAction[] = ['view', 'create', 'edit', 'delete'];

  const handleToggle = (module: AdminModule, action: PermissionAction) => {
    if (readOnly || disabledModules?.includes(module)) return;
    const current = modules[action] || [];
    const next = toggleModuleAction(current, module);
    onChange({ ...modules, [action]: next });
  };

  const isDifferent = (module: AdminModule, action: PermissionAction) => {
    if (!compareModules) return false;
    const current = moduleHasAction(modules[action] || [], module);
    const baseline = moduleHasAction(compareModules[action] || [], module);
    return current !== baseline;
  };

  return (
    <Box
      sx={{
        border: '1px solid #2a2a2a',
        borderRadius: 2,
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        pb: 2,
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                bgcolor: '#1a1a1a',
                color: '#94a3b8',
                fontWeight: 600,
                minWidth: 180,
                borderBottom: '1px solid #2a2a2a',
              }}
            >
              Module
            </TableCell>
            {actions.map((action) => (
              <TableCell
                key={action}
                align="center"
                sx={{
                  bgcolor: '#1a1a1a',
                  color: '#94a3b8',
                  fontWeight: 600,
                  borderBottom: '1px solid #2a2a2a',
                  width: 72,
                }}
              >
                {ACTION_LABELS[action]}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ALL_MODULES.map((module, index) => (
            <TableRow
              key={module}
              sx={{
                '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                '& td': {
                  borderBottom:
                    index === ALL_MODULES.length - 1
                      ? 'none'
                      : '1px solid #222',
                },
              }}
            >
              <TableCell sx={{ color: '#e2e8f0', py: 0.75 }}>
                <Typography variant="body2" fontSize={13}>
                  {MODULE_LABELS[module]}
                </Typography>
              </TableCell>
              {actions.map((action) => {
                const checked = moduleHasAction(modules[action] || [], module);
                const diff = isDifferent(module, action);
                const rowDisabled = readOnly || Boolean(disabledModules?.includes(module));
                return (
                  <TableCell key={action} align="center" sx={{ py: 0.5 }}>
                    <Tooltip
                      title={
                        disabledModules?.includes(module)
                          ? 'Only a founder can grant this module'
                          : diff
                            ? 'Differs from role default'
                            : checked
                              ? 'Granted'
                              : 'Not granted'
                      }
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        disabled={rowDisabled}
                        onChange={() => handleToggle(module, action)}
                        sx={{
                          color: diff ? '#f59e0b' : '#475569',
                          '&.Mui-checked': {
                            color: diff ? '#f59e0b' : '#3b82f6',
                          },
                          p: 0.5,
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default PermissionMatrix;
